import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../app_components/Icon';
import { fallbackAvatarOnError } from '../connect_sections/connectMemberHelpers';

export default function BrokenAvatarUsers({ missingImg }) {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalCandidates, setTotalCandidates] = useState(null);
  const [scannedCount, setScannedCount] = useState(0);
  const [avatarRemoved, setAvatarRemoved] = useState(false);
  const [removeError, setRemoveError] = useState(null);

  const fetchBatch = useCallback((batchPage, append) => {
    setLoading(true);
    $.ajax({
      url: '../get_broken_avatar_users',
      dataType: 'JSON',
      type: 'GET',
      data: {
        page: batchPage,
        batch_size: 50,
      },
      success: (data) => {
        const payload = data.data;
        setTotalCandidates(payload.total_candidates);
        setScannedCount((prev) => prev + payload.scanned);
        setHasMore(payload.has_more);

        setUsers((prev) => {
          if (!append) return payload.users;
          const seen = new Set(prev.map((u) => u.id));
          const merged = [...prev];
          payload.users.forEach((user) => {
            if (!seen.has(user.id)) merged.push(user);
          });
          return merged;
        });
        setLoading(false);
      },
      error: (xhr, status, err) => {
        console.log(err);
        setLoading(false);
      },
    });
  }, []);

  useEffect(() => {
    setUsers([]);
    setScannedCount(0);
    setPage(0);
    fetchBatch(0, false);
  }, [fetchBatch]);

  function scanNextBatch() {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchBatch(nextPage, true);
  }

  function selectUser(user) {
    setSelectedUser(user);
    setAvatarRemoved(false);
    setRemoveError(null);
  }

  function removeAvatar() {
    if (!selectedUser) return;

    setRemoveError(null);
    $.ajax({
      url: `../remove_avatar/${selectedUser.id}`,
      dataType: 'JSON',
      type: 'GET',
      success: () => {
        setAvatarRemoved(true);
        setUsers((prev) => prev.filter((u) => u.id !== selectedUser.id));
      },
      error: (xhr, status, err) => {
        console.log(err);
        setRemoveError('Could not remove avatar. Please try again.');
      },
    });
  }

  function renderTable() {
    if (loading && users.length === 0) {
      return <p>Scanning users for broken avatars…</p>;
    }

    if (users.length === 0 && !loading) {
      return (
        <p>
          No broken avatars found yet
          {scannedCount > 0 ? ` (scanned ${scannedCount} of ${totalCandidates} users with avatar filenames)` : ''}.
          {hasMore && ' Scan more to continue checking.'}
        </p>
      );
    }

    return (
      <table className="admin_table">
        <thead>
          <tr>
            <th />
            <th>Name</th>
            <th>Email</th>
            <th>Filename</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id}>
              <td style={{ verticalAlign: 'middle' }}>
                <img
                  src={user.avatar_url}
                  className="user_image"
                  style={{ height: 25, width: 25, borderRadius: '100%' }}
                  alt=""
                  onError={(e) => fallbackAvatarOnError(e, missingImg)}
                />
              </td>
              <td style={{ verticalAlign: 'middle' }}><p>{user.preferred_name}</p></td>
              <td style={{ verticalAlign: 'middle' }}><p>{user.email}</p></td>
              <td style={{ verticalAlign: 'middle' }}><p>{user.avatar_file_name}</p></td>
              <td style={{ verticalAlign: 'middle' }}>
                <p className={user.status === 'Cancelled' ? 'red' : 'darkBlue'}>{user.status}</p>
              </td>
              <td style={{ verticalAlign: 'middle', textAlign: 'end' }}>
                <button
                  type="button"
                  className="lex-btn lex-btn--secondary lex-btn--small"
                  onClick={() => selectUser(user)}
                >
                  select
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (selectedUser) {
    return (
      <div className="admin_section_outline">
        <button type="button" className="lex-btn lex-btn--ghost" onClick={() => setSelectedUser(null)}>
          {`< back`}
        </button>
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginTop: 10 }}>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <img
              src={selectedUser.avatar_url}
              className="user_image"
              style={{ height: 50, width: 50, borderRadius: '100%' }}
              alt=""
              onError={(e) => fallbackAvatarOnError(e, missingImg)}
            />
            <div style={{ paddingLeft: 10 }}>
              <p style={{ paddingBottom: 10 }}>{selectedUser.preferred_name}</p>
              <p>{selectedUser.email}</p>
              <p style={{ marginTop: 6, fontSize: 12, color: '#666' }}>
                {selectedUser.avatar_file_name}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', gap: 8 }}>
            {avatarRemoved ? (
              <p className="darkBlue">Avatar removed</p>
            ) : (
              <button
                type="button"
                className="lex-btn lex-btn--secondary lex-btn--small"
                onClick={removeAvatar}
              >
                Remove avatar
              </button>
            )}
            {removeError && <p className="red">{removeError}</p>}
            <a
              href={`/profile/${selectedUser.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="lex-btn lex-btn--ghost lex-btn--small"
            >
              View profile
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin_section_outline">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <h5 style={{ margin: 0 }}>Broken avatars</h5>
        <p style={{ margin: 0, fontSize: 13 }}>
          <strong>{users.length}</strong> broken
          {totalCandidates != null && (
            <span>
              {' '}
              · scanned {scannedCount} of {totalCandidates}
            </span>
          )}
        </p>
      </div>

      {renderTable()}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
        {hasMore && (
          <button
            type="button"
            className="lex-btn lex-btn--secondary lex-btn--small"
            disabled={loading}
            onClick={scanNextBatch}
          >
            {loading ? 'Scanning…' : 'Scan next batch'}
          </button>
        )}
        {!hasMore && scannedCount > 0 && (
          <p style={{ margin: 0, fontSize: 13 }}>Finished scanning all users with avatar filenames.</p>
        )}
        {loading && users.length > 0 && <Icon name="spinner" size={14} />}
      </div>
    </div>
  );
}
